// Cricket market RULES — the dispute-saver every exchange publishes. One small
// button, one modal, plain language. Shown on the lobby and the match screen.

import { useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { motion, AnimatePresence } from 'motion/react';

const SECTIONS: { icon: string; title: string; rules: string[] }[] = [
  {
    icon: 'emoji_events',
    title: 'Match Odds',
    rules: [
      'Settled on the official match result (super overs and DLS/VJD adjusted results count).',
      'Test matches: "The Draw" is a runner. If a market has no Draw runner and the match is drawn, all bets on that market are VOID (full refund).',
      'Abandoned / no-result matches: all bets VOID — full refunds, automatically.',
    ],
  },
  {
    icon: 'timeline',
    title: 'Sessions (Fancy)',
    rules: [
      'You bet YES (over) or NO (under) against the runs line shown when you place the bet. That line is locked onto YOUR bet even if the market line moves later.',
      'YES wins when the actual result is EQUAL TO OR ABOVE your line. NO wins when it is BELOW your line.',
      'Sessions suspend between deliveries — a locked session is temporary; it reopens when the bookmaker republishes the line.',
      'A session that never gets decided (innings ends early, abandoned) is VOID. Any session still unsettled 24 hours after the match ends is automatically VOIDED with full refunds.',
    ],
  },
  {
    icon: 'schedule',
    title: 'Live bet delay',
    rules: [
      'In-play bets are held for a few seconds before acceptance. If the price moves against you during the hold, your bet is NOT placed — you are asked to confirm the new price. If it moves in your favour, you get the better price.',
    ],
  },
  {
    icon: 'account_balance_wallet',
    title: 'Money & settlement',
    rules: [
      'Your at-risk amount is debited when the bet is accepted. Winnings are credited the moment the market settles — most markets settle automatically within seconds of the official result.',
      'Stake and exposure limits may apply to your account. A rejected bet always says exactly why.',
      'Voided bets always refund the full at-risk amount.',
    ],
  },
];

export default function RulesButton({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 rounded-lg font-bold text-arena-500 dark:text-arena-400 hover:text-arena-700 dark:hover:text-arena-200 bg-arena-100 dark:bg-arena-800 transition ${compact ? 'px-2 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'}`}
      >
        <Icon name="gavel" size={compact ? 11 : 13} /> Rules
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
            onClick={() => setOpen(false)}>
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ type: 'spring', damping: 22 }}
              className="bg-white dark:bg-arena-800 w-full sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 border border-arena-100 dark:border-white/10"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-black text-lg text-arena-900 dark:text-white flex items-center gap-2"><Icon name="gavel" size={18} className="text-brand-500" />Cricket Market Rules</h2>
                <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full bg-arena-100 dark:bg-arena-700 flex items-center justify-center text-arena-500"><Icon name="close" size={16} /></button>
              </div>
              <div className="space-y-4">
                {SECTIONS.map((s) => (
                  <div key={s.title}>
                    <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-brand-600 dark:text-brand-400 mb-1.5">
                      <Icon name={s.icon} size={13} />{s.title}
                    </div>
                    <ul className="space-y-1.5">
                      {s.rules.map((r, i) => (
                        <li key={i} className="flex gap-2 text-xs text-arena-600 dark:text-arena-300 leading-relaxed">
                          <span className="text-arena-300 dark:text-arena-600 shrink-0 mt-px">•</span>{r}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-arena-400 mt-4 pt-3 border-t border-arena-100 dark:border-white/5">Placing a bet means you accept these rules. Results follow the official record; obvious price errors may be voided.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
